import { useMemo, type FormEvent, type ReactNode } from "react";
import type { SourceTypeInfo } from "@/api/queries";
import {
  emptyValue,
  type OptionValue,
  type SourceFormValue,
  type SourceTypeKey,
} from "./formState";
import { Field } from "@/components/ui/Field";
import { Toggle } from "@/components/ui/Select";
import { Chip } from "@/components/ui/Chip";
import { ShieldIcon } from "@/components/icons";

interface SourceFormProps {
  sourceTypes: SourceTypeInfo[];
  value: SourceFormValue;
  onChange: (value: SourceFormValue) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  /** Hides the type picker and relabels the password field when editing. */
  editing?: boolean;
  hasStoredPassword?: boolean;
  error?: string | null;
  children?: ReactNode;
}

export function SourceForm({
  sourceTypes,
  value,
  onChange,
  onSubmit,
  editing = false,
  hasStoredPassword = false,
  error,
  children,
}: SourceFormProps) {
  const spec = useMemo(
    () => sourceTypes.find((entry) => entry.key === value.source_type),
    [sourceTypes, value.source_type],
  );

  const set = (patch: Partial<SourceFormValue>) => onChange({ ...value, ...patch });
  const setOption = (key: string, option: OptionValue) =>
    onChange({ ...value, options: { ...value.options, [key]: option } });

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-5">
      {!editing ? (
        <fieldset className="flex flex-col gap-3">
          <legend className="type-label text-ink mb-2">Source type</legend>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {sourceTypes.map((entry) => {
              const selected = entry.key === value.source_type;
              return (
                <button
                  key={entry.key}
                  type="button"
                  aria-pressed={selected}
                  onClick={() =>
                    onChange({
                      ...emptyValue(entry.key as SourceTypeKey),
                      name: value.name,
                      description: value.description,
                    })
                  }
                  className={[
                    "rounded-container border p-3 text-start motion-fast",
                    selected
                      ? "border-accent bg-selected"
                      : "border-border bg-card hover:border-border-emphasized hover:bg-hover",
                  ].join(" ")}
                >
                  <span className="type-label text-ink flex items-center gap-2">
                    {entry.label}
                    {!entry.has_native_comments ? <Chip tone="warning">no comments</Chip> : null}
                  </span>
                  <span className="type-support text-ink-secondary mt-1 block">
                    {entry.summary}
                  </span>
                </button>
              );
            })}
          </div>
        </fieldset>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Name"
          value={value.name}
          required
          placeholder="Warehouse (read replica)"
          onChange={(event) => set({ name: event.target.value })}
        />
        <Field
          label="Description"
          value={value.description}
          placeholder="What lives in this source"
          onChange={(event) => set({ description: event.target.value })}
        />
      </div>

      {spec && spec.fields.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {spec.fields.map((field) => {
            if (field.kind === "toggle") {
              return (
                <div key={field.name} className="flex items-center">
                  <Toggle
                    label={field.label}
                    hint={field.help || undefined}
                    checked={Boolean(value.options[field.name])}
                    onChange={(checked) => setOption(field.name, checked)}
                  />
                </div>
              );
            }
            const isCore = ["host", "port", "database", "username", "password", "file_path"].includes(
              field.name,
            );
            if (!isCore) {
              return (
                <Field
                  key={field.name}
                  label={field.label}
                  hint={field.help || undefined}
                  placeholder={field.placeholder}
                  value={String(value.options[field.name] ?? "")}
                  onChange={(event) => setOption(field.name, event.target.value)}
                />
              );
            }
            if (field.name === "password") {
              return (
                <Field
                  key={field.name}
                  label={field.label}
                  revealable
                  autoComplete="new-password"
                  placeholder={hasStoredPassword ? "Stored — leave blank to keep" : ""}
                  hint={
                    hasStoredPassword
                      ? "Encrypted at rest. Leave blank to keep the stored credential."
                      : field.help || undefined
                  }
                  value={value.password}
                  onChange={(event) => set({ password: event.target.value })}
                />
              );
            }
            const key = field.name as "host" | "port" | "database" | "username" | "file_path";
            // A blank port means "the engine's standard port", which the server
            // fills in — so the default is shown as a placeholder, not written
            // into the field where it would look like a deliberate choice.
            const placeholder =
              field.name === "port" && spec.default_port
                ? String(spec.default_port)
                : field.placeholder;
            return (
              <Field
                key={field.name}
                label={field.label}
                required={field.required}
                placeholder={placeholder}
                hint={field.help || undefined}
                inputMode={field.kind === "number" ? "numeric" : undefined}
                value={value[key]}
                onChange={(event) => set({ [key]: event.target.value } as Partial<SourceFormValue>)}
              />
            );
          })}
        </div>
      ) : null}

      {spec?.supports_upload ? (
        <p className="type-support text-ink-secondary rounded-element border border-border bg-surface p-3">
          {spec.key === "file"
            ? "This source is the file it was uploaded from. Replace it from the File tab."
            : "A DuckDB database is created and managed for this source, and you import CSV or TSV files into it from the File tab — several files make several tables. To query one file as it is, use Upload a file instead."}
        </p>
      ) : null}

      {spec?.read_only_enforcement ? (
        <p className="type-support text-ink-secondary flex items-start gap-2">
          <span className="text-ink-disabled mt-0.5">
            <ShieldIcon className="size-4" />
          </span>
          Queries run under SQLGuard and {spec.read_only_enforcement}. A read-only database login is
          still recommended.
        </p>
      ) : null}

      {error ? (
        <p role="alert" className="type-support text-error rounded-element bg-error-subtle p-3">
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">{children}</div>
    </form>
  );
}
